import { FieldValues, UseFormReturn } from "react-hook-form";
import { SpwkUtilV3 } from "../../dao/SpwkUtilV3";
import { UniForm } from "../../dao/UniForm";
import * as yup from "yup";
import { SpwkUtil } from "../../dao/SpwkUtil";
import { new_ErrorAlert } from "../../export_spwk/ExportManager";
import { UniInput, UniInputKind } from "../../dao/UniInput";
import { Is連動排除的題目, UniQuestion } from "../../dao/UniQuestion";
import { TypeOfShape } from "yup/lib/object";
import { UcHumanBodyImage_RHF_NAME_List } from "../../components/uni/UcHumanBodyImage";
import _ from "lodash";
import i18n from "../../../i18n";

export class CommonQuest_YupSchemaBuilder {
  static CreateSchema(
    rhf: UseFormReturn<FieldValues, any>,
    _CommonForm: UniForm
  ) {
    // yup格式:
    // const yup_schema: any = yup.object().shape({
    //   "m01": yup.object().shape({
    //     "多選題目1": yup.object().shape({
    //       // "選項1": yup.
    //     }),

    //     "基本資訊●工號●input_1": yup.string().required()
    //   })
    // });

    /* 示意圖:
    { <--- yup_shape

    作業經歷: { <--- yup_chapter
        基本資料●姓名●input_1: yup_input_valid() <--- 加入<非選必填>
        基本資料●性別●input_1: yup_input_valid() <--- 加入<非選必填>
        ...

      ┬ 工作班別: <--- 加入<單選必選>                         (單選: 固定/兩班/四班/其他:_____)
      └ 工作班別●其他●input_1: <--- 加入<單選有勾則必填> 
      ...

    }.test() { <--- 加入<多選必填驗證>

      errors = [];

      for All 多選題列表 {
        if(未勾選任何一個項目){
          errors.add(error)
        }          
      }

      return errors;
    }

  }
  */

    const yup_shape: any = {};
    for (let i = 1; i <= SpwkUtilV3.ANS_M_CNT; i++) {
      const chapter = "m" + ("" + i).padStart(2, "0"); // m01、m02....m20
      yup_shape[chapter] = CreateYupObjectByChapter(rhf, chapter, _CommonForm);
    }

    let yup_schema: any = yup.object().shape(yup_shape);

    return yup_schema;
  }
}

function CreateYupObjectByChapter(
  rhf: UseFormReturn<FieldValues, any>,
  chapter: string,
  _CommonForm: UniForm
) {
  // 範例:
  // let chapter_yup_shape = yup.object().shape({
  //   "基本資訊●工號●input_1": yup.string().required()
  // });

  let chapter_questions = SpwkUtil.GetChapterQuestionsV3(
    chapter,
    _CommonForm,
    []
  );
  chapter_questions = chapter_questions.filter(
    (q) => q.kind !== SpwkUtilV3.Q_KIND_標題
  );

  /** 此處排除畫面上不顯示的元件，看不到的不需驗證 */
  chapter_questions = chapter_questions.filter(
    (x) => !Is連動排除的題目(x, rhf)
  );

  let shape_非選必填 = GetShape_非選必填(chapter, chapter_questions);

  let shape_單選必填 = GetShape_單選必填(chapter, chapter_questions);
  let shape_單選有勾時_Input必填 = GetShape_單選有勾時_Input必填(
    chapter,
    chapter_questions
  );

  let shape_多選有勾時_Input必填 = GetShape_多選有勾時_Input必填(
    chapter,
    chapter_questions
  );

  // FIXME: 注意! 個驗證如果會重疊時，不可以這樣簡單加入，要另外小心的一一比對加入
  // yup.test().test()... 可能可以無限往後接，遇到不為空的節點，應該把 test() 往後加入
  // 正確的方法之後用到再上網找

  let chapter_yup_shape: any = {
    ...shape_非選必填,

    ...shape_單選必填,
    ...shape_單選有勾時_Input必填,

    ...shape_多選有勾時_Input必填,
  };

  /* 以下為拆解步驟: chapter_yup_object = yup.object().test(...).shape(...); */
  let chapter_yup_object = yup
    .object()
    .shape(chapter_yup_shape)
    .test("a1", "error message", (rhf_data) => {
      return Test_多選題至少勾一個(rhf_data, chapter, chapter_questions);
    })
    .test("a2", "人體圖必須選一個", (rhf_data) => {
      return Test_人體圖必須選一個(rhf_data, chapter, chapter_questions);
    });

  // 注意! 不知為何 test 一定要緊接在 shape 後面，否則不會觸發，以下方法不會正確觸發
  // let chapter_yup_object = yup.object().shape(chapter_yup_shape);
  // chapter_yup_object.test(...1);
  // chapter_yup_object.test(...2);

  return chapter_yup_object;
}

/**
 * 對所有非選題底下的 Inputs，加入 yup 驗證 (一般是判斷必填)
 */
function GetShape_非選必填(chapter: string, chapter_questions: UniQuestion[]) {
  /*
    示意圖:
    { <--- yup_shape

      作業經歷: { <--- yup_chapter

          基本資料●姓名●input_1: yup_input_valid() <--- 加入
          基本資料●性別●input_1: yup_input_valid() <--- 加入
          ...
      }
    }

  */
  const yup_chapter: any = {};

  const questions = chapter_questions.filter(
    (q) => q.kind === SpwkUtil.KIND_非選
  );

  for (const question of questions) {
    if (question.Options == null) {
      throw new_ErrorAlert("question.Options == null");
    }

    for (const option of question.Options) {
      if (option.Inputs == null) {
        throw new_ErrorAlert("option.Inputs == null");
      }

      for (const input of option.Inputs) {
        let input_rhf_name = option.get_Input_RhfName(input.input_code);
        input_rhf_name = RemoveChapterPrefix(input_rhf_name, chapter);

        yup_chapter[input_rhf_name] = YupValid_Input必填(input);
      }
    }
  }

  return yup_chapter;
}

function GetShape_單選必填(chapter: string, chapter_questions: UniQuestion[]) {
  const yup_chapter: any = {};

  /** 單選 必填 */
  const questions = chapter_questions.filter(
    (q) => q.kind === SpwkUtil.KIND_單選
  );
  for (const question of questions) {
    yup_chapter[question.question_code] = YupValid_單選必填(question);
  }

  return yup_chapter;
}

function YupValid_單選必填(question: UniQuestion) {
  if (question.kind !== SpwkUtilV3.Q_KIND_單選) {
    throw new_ErrorAlert("616843151 必須是單選題 !!!");
  }

  return yup.string().test("", "error message", (rhf_value) => {
    if (rhf_value == null || rhf_value === "") {
      // 單選 未填寫數值
      let e1 = new yup.ValidationError(
        `必須填寫!`,
        null,
        `${question.get_Question_RhfName()}`
      ); //單選題沒選擇, 丟出錯誤訊息
      return new yup.ValidationError([e1]);
    } else {
      return true;
    }
  });
}

/**
 * 原始rhf_name = 基本資料.基本資料●身分字號●input_1
 * 移除開頭的: "基本資料." 才能正確加入 yup 結構
 */
function RemoveChapterPrefix(rhf_name: string, chapter: string): string {
  return rhf_name.substring((chapter + ".").length);
}

/**
 * 取得單一 Input 的 yup驗證
 */
function YupValid_Input必填(input: UniInput) {
  if (input.input_required === "0") {
    return yup.string(); // 不用限制必填
  }

  if (input.input_kind === UniInputKind.Text) {
    return yup.string().required("必填!");
  } else if (input.input_kind === UniInputKind.Select) {
    return yup.string().required("必填!");
  } else if (input.input_kind === UniInputKind.DateTime) {
    return yup.string().nullable().required("必填!");
  } else if (input.input_kind === UniInputKind.Number) {
    return yup.string().nullable().required("必填!");
  } else if (input.input_kind === UniInputKind.Int) {
    return yup.string().nullable().required("必填!");
  } else {
    throw new_ErrorAlert(
      `未知的 input.input_kind=${input.input_kind} !!!\ninput.guid: ${input.guid}`
    );
  }
}

function GetShape_單選有勾時_Input必填(
  chapter: string,
  chapter_questions: UniQuestion[]
) {
  const yup_chapter: any = {};

  const questions = chapter_questions.filter(
    (q) => q.kind === SpwkUtil.KIND_單選
  );

  /** 單選: 有勾時，要依照有 Input 條件驗證，必須合法 */
  for (const question of questions) {
    if (question.Options == null) {
      throw new_ErrorAlert("4949465 question.Options ==null !!!");
    }

    for (const option of question.Options) {
      if (option.Inputs == null) {
        throw new_ErrorAlert("option.Inputs == null !!!");
      }

      if (option.Inputs.length === 0) {
        continue; // pass; 沒有 Input，不須處理
      }

      for (const input of option.Inputs) {
        let q_rhf_name = question.get_Question_RhfName();
        q_rhf_name = RemoveChapterPrefix(q_rhf_name, chapter); // 其實目前相當於 question_code

        let input_rhf_name = option.get_Input_RhfName(input.input_code);
        input_rhf_name = RemoveChapterPrefix(input_rhf_name, chapter); // 排除 "章節名稱." 開頭的 rhf_name

        yup_chapter[input_rhf_name] = yup.string().when([q_rhf_name], {
          is: input.option_code, // if(rhf_value === "答案") {... =有勾選時
          then: YupValid_Input必填(input), // 才觸發這個驗證
        });
      }
    }
  }

  return yup_chapter;
}

function GetShape_多選有勾時_Input必填(
  chapter: string,
  chapter_questions: UniQuestion[]
) {
  const yup_chapter: any = {};

  const questions = chapter_questions.filter(
    (q) => q.kind === SpwkUtil.KIND_多選
  );

  /** 多選: 有勾時，要依照有 Input 條件驗證，必須合法 */
  for (const question of questions) {
    if (question.Options == null) {
      throw new_ErrorAlert("4949465 question.Options ==null !!!");
    }

    for (const option of question.Options) {
      if (option.Inputs == null) {
        throw new_ErrorAlert("1468431 option.Inputs == null !!!");
      }

      if (option.Inputs.length === 0) {
        continue; // pass; 沒有 Input，不須處理
      }

      for (const input of option.Inputs) {
        let option_rhf_name = option.get_Option_RhfName();
        option_rhf_name = RemoveChapterPrefix(option_rhf_name, chapter); // 排除 "章節名稱." 開頭的 rhf_name

        let input_rhf_name = option.get_Input_RhfName(input.input_code);
        input_rhf_name = RemoveChapterPrefix(input_rhf_name, chapter); // 排除 "章節名稱." 開頭的 rhf_name

        let target_name = option_rhf_name; // 誰勾起來，才要判斷必填 rhf[target_name]===true 時，才要判斷必填
        yup_chapter[input_rhf_name] = yup.string().when([target_name], {
          is: true, // if(rhf_value === "答案") {... =有勾選時
          then: YupValid_Input必填(input), // 才觸發這個驗證
        });
      }
    }
  }

  return yup_chapter;
}

function Test_多選題至少勾一個(
  rhf_data: TypeOfShape<any>,
  chapter: string,
  chapter_questions: UniQuestion[]
): boolean | yup.ValidationError | Promise<boolean | yup.ValidationError> {
  let questions = chapter_questions.filter(
    (q) => q.kind === SpwkUtilV3.Q_KIND_多選
  );

  let yup_errors: yup.ValidationError[] = [];

  for (const question of questions) {
    let has_check = Is此多選題是否至少勾選一個項目(chapter, rhf_data, question);

    if (has_check === false) {
      let e1 = new yup.ValidationError(
        i18n.t(`至少選一個`),
        null,
        question.get_Question_RhfName()
      );
      yup_errors.push(e1);
    }
  }

  if (yup_errors.length === 0) {
    return true;
  } else {
    return new yup.ValidationError(yup_errors);
  }
}

function Test_人體圖必須選一個(
  rhf_data: TypeOfShape<any>,
  chapter: string,
  chapter_questions: UniQuestion[]
): boolean | yup.ValidationError | Promise<boolean | yup.ValidationError> {
  let q_list = chapter_questions.filter(
    (x) =>
      x.kind === SpwkUtilV3.Q_KIND_自訂 &&
      x.question_title === SpwkUtilV3.UcHumanBodyImage
  );

  if (q_list.length === 0) {
    return true; // 非人體圖不須處理
  }

  let question = q_list[0];

  let data: any = {};
  data["" + question.chapter] = rhf_data;

  //#region 判斷是否全部沒勾
  let is_all_blank = true;
  for (const rhf_name of UcHumanBodyImage_RHF_NAME_List) {
    let val = _.get(data, rhf_name);
    if (val != null && ("" + val).trim() !== "") {
      is_all_blank = false;
      break;
    }
  }
  //#endregion

  if (is_all_blank === false) {
    return true; // valid pass;
  } else {
    let yup_errors: yup.ValidationError[] = [];
    let e1 = new yup.ValidationError(
      i18n.t(`人體圖 至少選一個!`),
      null,
      question.get_Question_RhfName()
    );
    yup_errors.push(e1);

    return new yup.ValidationError(yup_errors);
  }
}

function Is此多選題是否至少勾選一個項目(
  chapter: string,
  rhf_data: any,
  question: UniQuestion
) {
  let q_data_obj = undefined;

  if (
    chapter === SpwkUtilV3.CHAPTER_既往病史 ||
    chapter === SpwkUtilV3.CHAPTER_自覺症狀
  ) {
    // CH4、CH6 這兩大題，答案都混在這個 data
    // 下面再對所有 此傳入的單一Question.Options 檢查，是否至少有一個選項是true
    q_data_obj = rhf_data;
  } else {
    // 對於一般的題目，個別答案在個別的 欄位中
    // 先取出來，再檢查 Options 檢查，是否至少有一個選項是true
    q_data_obj = rhf_data[question.question_code];
  }

  let has_check = false;

  if (q_data_obj == null) {
    has_check = false; // 可能還沒填寫
  } else {
    if (question.Options == null) {
      throw new_ErrorAlert("3541616 question.Options==null !!!");
    }

    const fields = question.Options.map((x) => x.option_code); // ["造船", "開礦", "紡織"... ]

    for (const field of fields) {
      let tf = q_data_obj[field] as boolean;
      if (tf === true) {
        has_check = true;
        break;
      }
    }
  }

  return has_check;
}
